import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CircleCheck, CircleX, TerminalSquare } from 'lucide-react';
import { ApiError, api, seg } from '../api/client';
import type { MeResponse } from '../api/queries';
import { ErrorAlert } from '../components/ErrorAlert';
import { RobotMark } from '../components/Logo';
import { RelativeTime } from '../components/RelativeTime';
import { ThemeToggle } from '../components/ThemeToggle';
import { Haptic } from '../kit/haptics';
import { haptic } from '../kit/native';

interface DeviceInfo {
  userCode: string;
  clientName: string;
  status: string;
  expiresAt: string;
}

/** Approves a CLI device-authorization request (`devrobot login --device`). */
export default function DevicePage() {
  const [params, setParams] = useSearchParams();
  const result = params.get('result');
  const initial = (params.get('code') ?? '').trim().toUpperCase();
  const [code, setCode] = useState(initial);
  const [lookup, setLookup] = useState(initial);

  const me = useQuery({ queryKey: ['me', 'device'], queryFn: () => api.get<MeResponse>('/api/v1/me', { allowUnauthenticated: true }), retry: false });
  const signedOut = me.error instanceof ApiError && me.error.status === 401;

  useEffect(() => {
    if (signedOut && !result) window.location.assign(`/auth/device/login${lookup ? `?code=${encodeURIComponent(lookup)}` : ''}`);
  }, [signedOut, lookup, result]);

  const info = useQuery({
    queryKey: ['device', lookup],
    queryFn: () => api.get<DeviceInfo>(`/api/v1/auth/device/${seg(lookup)}`, { allowUnauthenticated: true }),
    enabled: Boolean(lookup) && me.isSuccess && !result,
    retry: false,
  });

  const decide = useMutation({
    mutationFn: (approve: boolean) => api.post<{ ok: true }>('/api/v1/auth/device/approve', { userCode: lookup, approve }, { allowUnauthenticated: true }),
    onSuccess: (_r, approve) => setParams({ result: approve ? 'approved' : 'denied' }),
    onError: () => haptic('error'),
  });

  let body: React.ReactNode;
  if (result === 'approved' || result === 'denied') {
    const ok = result === 'approved';
    body = (
      <div className="grid animate-pop justify-items-center gap-3 text-center">
        <span className={`${ok ? 'dr-tint-success' : 'dr-tint-error'} grid size-16 place-items-center rounded-full`}>
          {ok ? <CircleCheck className="size-8" aria-hidden="true" /> : <CircleX className="size-8" aria-hidden="true" />}
        </span>
        <h2 className="text-xl font-semibold tracking-tight">{ok ? 'Device approved' : 'Request denied'}</h2>
        <p className="text-sm text-base-content/65">
          {ok ? 'The DevRobot CLI is now signed in as you. You can close this tab and return to your terminal.' : 'The CLI was not signed in. If you did not start this request, no further action is needed.'}
        </p>
        <Link to="/" className="btn btn-ghost btn-sm">
          Open the administrator
        </Link>
      </div>
    );
  } else if (me.isPending || signedOut) {
    body = (
      <div className="flex justify-center py-6" role="status">
        <span className="loading loading-dots loading-md" aria-label={signedOut ? 'Redirecting to sign-in' : 'Checking your session'} />
      </div>
    );
  } else if (me.isError) {
    body = <ErrorAlert error={me.error} onRetry={() => void me.refetch()} />;
  } else {
    body = (
      <div className="space-y-4">
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            setLookup(code.trim().toUpperCase());
          }}
        >
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Code shown in your terminal</legend>
            <div className="join w-full">
              <input
                className="input input-lg join-item w-full font-mono uppercase tracking-widest"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="ABCD-EFGH"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                aria-label="Device code"
              />
              <button type="submit" className={`btn btn-lg join-item ${info.data?.status === 'pending' ? '' : 'btn-primary'}`} disabled={!code.trim()}>
                Look up
              </button>
            </div>
          </fieldset>
        </form>
        {info.isFetching ? <span className="loading loading-dots loading-sm text-base-content/60" aria-label="Looking up the code" /> : null}
        {info.isError ? (
          <ErrorAlert error={info.error} title={info.error instanceof ApiError && info.error.status === 404 ? 'No request with this code' : undefined} />
        ) : null}
        {info.data ? (
          <div className="animate-rise space-y-3 rounded-box bg-base-200/60 p-4">
            <div className="flex items-center gap-3">
              <span className="dr-tint-primary grid size-10 shrink-0 place-items-center rounded-field">
                <TerminalSquare className="size-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <div className="font-semibold">{info.data.clientName || 'DevRobot CLI'}</div>
                <div className="text-sm text-base-content/60">
                  Code <span className="font-mono">{info.data.userCode}</span> · expires <RelativeTime value={info.data.expiresAt} />
                </div>
              </div>
            </div>
            {info.data.status === 'pending' ? (
              <>
                <p className="text-sm">
                  Approving signs this CLI in as <strong>{me.data.user.name}</strong> with your GitLab permissions. Approve only if you started <code className="font-mono">devrobot login</code> yourself and the code matches your
                  terminal.
                </p>
                <ErrorAlert error={decide.error} />
                <div className="flex flex-wrap justify-end gap-2">
                  <Haptic disabled={decide.isPending}>
                    <button type="button" className="btn btn-ghost dr-ink-error" disabled={decide.isPending} onClick={() => decide.mutate(false)}>
                      Deny
                    </button>
                  </Haptic>
                  <Haptic disabled={decide.isPending}>
                    <button type="button" className="btn btn-primary" disabled={decide.isPending} onClick={() => decide.mutate(true)}>
                      {decide.isPending ? <span className="loading loading-spinner loading-sm" aria-hidden="true" /> : null}
                      Approve
                    </button>
                  </Haptic>
                </div>
              </>
            ) : (
              <p role="status" className="text-sm">
                This request is <strong>{info.data.status}</strong>
                {info.data.status === 'expired' ? '. Run devrobot login again to get a new code.' : ' and can no longer be changed.'}
              </p>
            )}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <main className="relative flex min-h-dvh flex-col bg-canvas dr-aurora dr-safe-px-4 dr-safe-pt-4 dr-safe-pb-6">
      <div className="flex justify-end">
        <ThemeToggle />
      </div>
      <div className="flex flex-1 items-center justify-center py-8">
        <div className="card w-full max-w-md animate-rise dr-surface">
          <div className="card-body gap-5 p-6 sm:p-8">
            <div className="flex items-center gap-3">
              <RobotMark className="size-12" />
              <div>
                <h1 className="text-xl leading-tight font-bold tracking-tight">
                  Authorize the <span className="dr-dusk-text">DevRobot</span> CLI
                </h1>
                <p className="text-sm text-base-content/60">Device sign-in</p>
              </div>
            </div>
            {body}
          </div>
        </div>
      </div>
    </main>
  );
}
